import { supabase } from '../../../foundation/altzero_base/base/utils/supabaseClient';
import type {
  SalesforceImportPayload,
  NormalizedSalesforceImport,
  FieldMappingEntry,
} from '../types/salesforceTypes';

const API_KEY = 'scopingai';

// App-valid Retailer Comms values (from siteTypes.ts RETAILER_COMMS)
const APP_RETAILER_COMMS = ['0%', '0.1c', '0.2c', '0.4c', '1.0%', '1.5%', '2.0%', '2.5%', '3.0%', '3.5%'];

// Normalize SF Retail_Comms__c picklist value → app RETAILER_COMMS value
// SF stores "1.00%" but app uses "1.0%". Also drops DMA/GAS/RESI/SME.
const normalizeRetailerComms = (sfValue: string | null): string => {
  if (!sfValue) return '';
  const mapping: Record<string, string> = {
    '1.00%': '1.0%',
    '1.50%': '1.5%',
    '2.00%': '2.0%',
    '2.50%': '2.5%',
    '3.00%': '3.0%',
    '3.50%': '3.5%',
  };
  const normalized = mapping[sfValue] ?? sfValue;
  return APP_RETAILER_COMMS.includes(normalized) ? normalized : '';
};

// Parse a YYYY-MM-DD date string to a Date object
const parseDate = (s: string | null): Date | null => (s ? new Date(s) : null);

// Build a FieldMappingEntry for the audit log
const makeEntry = (
  sfSource: string,
  sfRawValue: string | number | boolean | null | undefined,
  appField: string,
  mappedValue: string | number | boolean | null,
  normalized = false
): FieldMappingEntry => {
  const raw = sfRawValue !== undefined && sfRawValue !== null ? String(sfRawValue) : null;
  const mapped = mappedValue !== null && mappedValue !== undefined ? String(mappedValue) : null;
  let status: FieldMappingEntry['status'] = 'skipped';
  if (mapped) status = normalized ? 'normalized' : 'mapped';
  return { sfSource, sfRawValue: raw, appField, mappedValue: mapped, status };
};

export const salesforceImportService = {
  // Fetch combined import payload from backend
  importFromOpportunity: async (
    opportunityId: string,
    idType: 'opportunity' | 'tender' = 'opportunity'
  ): Promise<{ success: boolean; data: SalesforceImportPayload | null; error: string | null }> => {
    try {
      const response = await fetch(`/api/salesforce/import/opportunity/${opportunityId}?idType=${idType}`, {
        headers: { 'x-api-key': API_KEY },
      });
      const result = await response.json();
      if (!result.success) {
        return { success: false, data: null, error: result.error || 'Import failed' };
      }
      return { success: true, data: result.data as SalesforceImportPayload, error: null };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      return { success: false, data: null, error: message };
    }
  },

  // Normalize raw SF payload into app-ready values with mapping audit log
  normalizePayload: (payload: SalesforceImportPayload): NormalizedSalesforceImport => {
    const { opportunity, tender } = payload;
    const log: FieldMappingEntry[] = [];

    // --- Client Name ---
    const clientName = opportunity.accountName ?? null;
    log.push(makeEntry('Opportunity > Account.Name', opportunity.accountName, 'Client Name', clientName));

    // --- SF Account ID ---
    const sfAccountId = opportunity.accountId ?? null;
    log.push(makeEntry('Opportunity > AccountId', opportunity.accountId, 'SF Account ID', sfAccountId));

    // --- Contact details ---
    const contactName = opportunity.contactName ?? null;
    log.push(makeEntry('Opportunity > Metering_Contact_Name__c', opportunity.contactName, 'Contact Name', contactName));
    const contactEmail = opportunity.contactEmail ?? null;
    log.push(makeEntry('Opportunity > Metering_Contact_Email__c', opportunity.contactEmail, 'Contact Email', contactEmail));
    const accountPhone = opportunity.accountPhone ?? null;
    log.push(makeEntry('Account > Phone', opportunity.accountPhone, 'Phone', accountPhone));

    // --- SF IDs ---
    const sfOpportunityId = opportunity.id;
    const sfTenderId = tender?.id ?? null;

    // --- Proposal / Entity Name ---
    const opportunityName = opportunity.name ?? null;
    const tenderNameValue = tender?.name ?? null;
    const entityName = tender?.entityName ?? null;
    log.push(makeEntry('Opportunity > Name', opportunity.name, 'Proposal Name (suggestion)', opportunityName));
    log.push(makeEntry('Tender__c > Entity_name__c', tender?.entityName, 'Entity Name', entityName));

    // --- ABN / ACN (Tender primary, Opportunity fallback) ---
    const abn = tender?.abn ?? opportunity.abn ?? null;
    const abnSource = tender?.abn != null ? 'Tender__c > ABN__c' : 'Opportunity > ABN__c';
    log.push(makeEntry(abnSource, abn, 'ABN', abn));
    const acn = tender?.acn ?? null;
    log.push(makeEntry('Tender__c > ACN__c', tender?.acn, 'ACN', acn));

    // --- NMI (Tender primary, Opportunity fallback) ---
    const nmi = tender?.nmi ?? opportunity.nmi ?? null;
    const nmiSource = tender?.nmi != null ? 'Tender__c > NMI__c' : 'Opportunity > N_M_I__c';
    log.push(makeEntry(nmiSource, nmi, 'NMI', nmi));

    // --- Energy kWh (Tender primary, Opportunity fallback) ---
    const annualKwhPeak = tender?.annualKwhPeak ?? opportunity.annualKwhPeak ?? null;
    const annualKwhPeakSource = tender?.annualKwhPeak != null ? 'Tender__c > Peak_Annual_kWh_Usage__c' : 'Opportunity > Peak_Annual_kWh_Usage__c';
    log.push(makeEntry(annualKwhPeakSource, annualKwhPeak, 'Annual kWh Peak', annualKwhPeak));

    const annualKwhOffpeak = tender?.annualKwhOffpeak ?? opportunity.annualKwhOffpeak ?? null;
    const annualKwhOffpeakSource = tender?.annualKwhOffpeak != null ? 'Tender__c > Off_Peak_Annual_kWh_Usage__c' : 'Opportunity > Off_Peak_Annual_kWh_Usage__c';
    log.push(makeEntry(annualKwhOffpeakSource, annualKwhOffpeak, 'Annual kWh Off-Peak', annualKwhOffpeak));

    const annualKwhShoulder = tender?.annualKwhShoulder ?? opportunity.annualKwhShoulder ?? null;
    const annualKwhShoulderSource = tender?.annualKwhShoulder != null ? 'Tender__c > Shoulder_Annual_kWh_Usage__c' : 'Opportunity > Shoulder_Annual_kWh_Usage__c';
    log.push(makeEntry(annualKwhShoulderSource, annualKwhShoulder, 'Annual kWh Shoulder', annualKwhShoulder));

    const annualKwhTotal = tender?.annualKwhTotal ?? opportunity.annualKwhTotal ?? null;
    const annualKwhTotalSource = tender?.annualKwhTotal != null ? 'Tender__c > Total_Annual_kWh_Usage__c' : 'Opportunity > Total_Annual_kWh_Usage__c';
    log.push(makeEntry(annualKwhTotalSource, annualKwhTotal, 'Annual kWh Total', annualKwhTotal));

    // --- Dates ---
    const rawContractExpiry = tender?.contractExpiry ?? null;
    const currentContractExpiry = parseDate(rawContractExpiry);
    log.push(makeEntry('Tender__c > Current_Contract_Expiry__c', rawContractExpiry, 'Current Contract Expiry', rawContractExpiry));

    const rawDueDate = tender?.tenderDueDate ?? null;
    const tenderDueDate = parseDate(rawDueDate);
    log.push(makeEntry('Tender__c > Tender_Due_Date__c', rawDueDate, 'Due Date', rawDueDate));

    // --- Current meter / retailer ---
    const currentMeterProvider = tender?.currentMeterProvider ?? null;
    log.push(makeEntry('Tender__c > Current_Meter_Provider__c', tender?.currentMeterProvider, 'Current Meter Provider', currentMeterProvider));

    const currentRetailer = tender?.currentRetailer ?? null;
    log.push(makeEntry('Tender__c > Current_Retailer__c', tender?.currentRetailer, 'Current Retailer', currentRetailer));

    // --- DMA Provider ---
    const dmaProvider = tender?.dmaProvider ?? null;
    log.push(makeEntry('Tender__c > DMA_Provider__c', tender?.dmaProvider, 'DMA Provider', dmaProvider));

    // --- Retailer Comms (normalized) ---
    const rawComms = tender?.retailerComms ?? null;
    const retailerComms = normalizeRetailerComms(rawComms);
    const commsNormalized = rawComms !== null && rawComms !== retailerComms;
    log.push(makeEntry('Tender__c > Retail_Comms__c', rawComms, 'Retailer Comms', retailerComms || null, commsNormalized));

    // --- Address ---
    const address = {
      street: tender?.address?.street ?? null,
      city: tender?.address?.city ?? null,
      state: tender?.address?.state ?? null,
      postalCode: tender?.address?.postalCode ?? null,
      country: tender?.address?.country ?? null,
    };
    log.push(makeEntry('Tender__c > Address__Street__s', tender?.address?.street, 'Address Street', address.street));
    log.push(makeEntry('Tender__c > Address__City__s', tender?.address?.city, 'Address City', address.city));
    log.push(makeEntry('Tender__c > Address__StateCode__s', tender?.address?.state, 'Address State', address.state));
    log.push(makeEntry('Tender__c > Address__PostalCode__s', tender?.address?.postalCode, 'Address Postcode', address.postalCode));
    log.push(makeEntry('Tender__c > Address__CountryCode__s', tender?.address?.country, 'Address Country', address.country));

    // --- Configuration flags ---
    const createDma = tender?.createDma ?? null;
    log.push(makeEntry('Tender__c > Create_DMA__c', tender?.createDma, 'Create DMA', createDma));

    const requestMeterData = tender?.requestMeterData ?? null;
    log.push(makeEntry('Tender__c > Request_Meter_Data__c', tender?.requestMeterData, 'Request Meter Data', requestMeterData));

    const requestBills = tender?.requestBills ?? null;
    log.push(makeEntry('Tender__c > Request_Bills__c', tender?.requestBills, 'Request Bills', requestBills));

    const acquireCed = tender?.acquireCed ?? null;
    log.push(makeEntry('Tender__c > Acquire_CED__c', tender?.acquireCed, 'Acquire CED', acquireCed));

    const greenPower = tender?.greenPower ?? null;
    log.push(makeEntry('Tender__c > Green_Power__c', tender?.greenPower, 'Green Power', greenPower));

    const retailerNotes = tender?.retailerNotes ?? null;
    log.push(makeEntry('Tender__c > Notes__c', tender?.retailerNotes, 'Retailer Notes', retailerNotes));

    const driveLink = tender?.driveLink ?? null;
    log.push(makeEntry('Tender__c > Drive_Link__c', tender?.driveLink, 'Drive Link', driveLink));

    const consultant = payload.consultant ?? null;

    // --- Site fields from Opportunity ---
    const loaExpiryDate = opportunity.loaExpiryDate ?? null;
    log.push(makeEntry('Opportunity > LOA_Expiry_Date__c', opportunity.loaExpiryDate, 'LOA Expiry Date', loaExpiryDate));
    const dmaStatus = opportunity.dmaStatus ?? null;
    log.push(makeEntry('Opportunity > DMA_Status__c', opportunity.dmaStatus, 'DMA Status', dmaStatus));
    const meterInstallDate = opportunity.meterInstallDate ?? null;
    log.push(makeEntry('Opportunity > DMA_Installation_date__c', opportunity.meterInstallDate, 'Meter Install Date', meterInstallDate));
    const dmaType = opportunity.dmaType ?? null;
    log.push(makeEntry('Opportunity > DMA_Type__c', opportunity.dmaType, 'DMA Type', dmaType));
    const dmaStartDate = opportunity.dmaStartDate ?? null;
    log.push(makeEntry('Opportunity > Metering_Contract_Start_Date__c', opportunity.dmaStartDate, 'DMA Start Date', dmaStartDate));
    const dmaEndDate = opportunity.dmaEndDate ?? null;
    log.push(makeEntry('Opportunity > Metering_Contract_End_Date__c', opportunity.dmaEndDate, 'DMA End Date', dmaEndDate));
    const shieldPlatinum = opportunity.shieldPlatinum ?? null;
    log.push(makeEntry('Opportunity > SHIELD_Platinum__c', opportunity.shieldPlatinum, 'Shield Platinum', shieldPlatinum));
    const siteDriveLink = opportunity.siteDriveLink ?? null;
    log.push(makeEntry('Opportunity > Shield_Drive_Link__c', opportunity.siteDriveLink, 'Site Drive Link', siteDriveLink));

    return {
      clientName,
      sfAccountId,
      contactName,
      contactEmail,
      accountPhone,
      abn,
      acn,
      opportunityName,
      tenderName: tenderNameValue,
      entityName,
      sfOpportunityId,
      sfTenderId,
      nmi,
      annualKwhPeak,
      annualKwhOffpeak,
      annualKwhShoulder,
      annualKwhTotal,
      currentContractExpiry,
      currentMeterProvider,
      currentRetailer,
      dmaProvider,
      retailerComms,
      tenderDueDate,
      address,
      createDma,
      requestMeterData,
      requestBills,
      acquireCed,
      greenPower,
      retailerNotes,
      driveLink,
      loaExpiryDate,
      dmaStatus,
      meterInstallDate,
      dmaType,
      dmaStartDate,
      dmaEndDate,
      shieldPlatinum,
      siteDriveLink,
      consultant,
      fieldMappingLog: log,
    };
  },

  // Check if an import already exists for this tender_id
  checkImportExists: async (
    tenderId: string
  ): Promise<{ exists: boolean; importedAt: string | null; sfOpportunityId: string | null }> => {
    try {
      const { data } = await supabase
        .from('salesforce_import')
        .select('imported_at, sf_opportunity_id')
        .eq('tender_id', tenderId)
        .single();
      return {
        exists: !!data,
        importedAt: data?.imported_at ?? null,
        sfOpportunityId: data?.sf_opportunity_id ?? null,
      };
    } catch {
      return { exists: false, importedAt: null, sfOpportunityId: null };
    }
  },

  // Store (or update) import record in Supabase — upsert on tender_id
  storeImportRecord: async (
    tenderId: string,
    sfOpportunityId: string,
    sfTenderId: string | null,
    payload: SalesforceImportPayload,
    fieldMapping: FieldMappingEntry[]
  ): Promise<{ success: boolean; error: string | null }> => {
    try {
      const { error } = await supabase
        .from('salesforce_import')
        .upsert(
          {
            tender_id: tenderId,
            sf_opportunity_id: sfOpportunityId,
            sf_tender_id: sfTenderId,
            import_payload: payload,
            field_mapping: fieldMapping,
            imported_at: new Date().toISOString(),
          },
          { onConflict: 'tender_id' }
        );
      return { success: !error, error: error?.message ?? null };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      return { success: false, error: message };
    }
  },

  // Fetch SF fields for an existing site and write them to site, bv_dma, and contact tables.
  // Called when the Sites page detects missing SF-sourced fields.
  syncSiteFieldsFromSf: async (
    siteId: string,
    clientId: string,
    orgId: string,
    sfOpportunityId: string
  ): Promise<{ success: boolean; error: string | null }> => {
    try {
      const result = await salesforceImportService.importFromOpportunity(sfOpportunityId);
      if (!result.success || !result.data) {
        return { success: false, error: result.error };
      }
      const { opportunity } = result.data;

      // Update site with SF-sourced fields (only overwrite if SF has a value)
      const siteUpdate: Record<string, unknown> = {};
      if (opportunity.nmi) siteUpdate.nmi = opportunity.nmi;
      if (opportunity.dmaStatus) siteUpdate.dma_status = opportunity.dmaStatus;
      if (opportunity.meterInstallDate) siteUpdate.meter_install_date = opportunity.meterInstallDate;
      if (opportunity.loaExpiryDate) siteUpdate.loa_expiry_date = opportunity.loaExpiryDate;
      if (opportunity.shieldPlatinum !== null) siteUpdate.shield_platinum = opportunity.shieldPlatinum;
      if (opportunity.siteDriveLink) siteUpdate.drive_link = opportunity.siteDriveLink;

      if (Object.keys(siteUpdate).length > 0) {
        await supabase.from('site').update(siteUpdate).eq('id', siteId);
      }

      // Upsert bv_dma if DMA fields are present
      if (opportunity.dmaType || opportunity.dmaStartDate || opportunity.dmaEndDate) {
        const { data: existing } = await supabase
          .from('bv_dma')
          .select('id')
          .eq('site_id', siteId)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        const dmaPayload = {
          org_id: orgId,
          site_id: siteId,
          dma_provider: opportunity.dmaType ?? null,
          start_date: opportunity.dmaStartDate ?? null,
          end_date: opportunity.dmaEndDate ?? null,
          updated_at: new Date().toISOString(),
        };

        if (existing?.id) {
          await supabase.from('bv_dma').update(dmaPayload).eq('id', existing.id);
        } else {
          await supabase.from('bv_dma').insert({ ...dmaPayload, created_at: new Date().toISOString() });
        }
      }

      // Upsert metering contact if name or email present
      if (opportunity.contactName || opportunity.contactEmail) {
        const nameParts = (opportunity.contactName ?? '').trim().split(/\s+/);
        const firstName = nameParts[0] || null;
        const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : null;

        const { data: existingContact } = await supabase
          .from('contact')
          .select('id')
          .eq('client_id', clientId)
          .eq('role', 'metering')
          .limit(1)
          .single();

        const contactPayload = {
          org_id: orgId,
          client_id: clientId,
          first_name: firstName,
          last_name: lastName,
          email: opportunity.contactEmail ?? null,
          role: 'metering',
          extn: {},
          bv_extn: {},
        };

        if (existingContact?.id) {
          await supabase.from('contact').update(contactPayload).eq('id', existingContact.id);
        } else {
          await supabase.from('contact').insert({ ...contactPayload, created_at: new Date().toISOString() });
        }
      }

      return { success: true, error: null };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      return { success: false, error: message };
    }
  },

  // Fire-and-forget: update Stage__c on a Salesforce Tender__c record
  updateTenderStageInSf: async (sfTenderId: string, stage: string): Promise<void> => {
    try {
      await fetch(`/api/salesforce/tenders/${sfTenderId}/stage`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
        body: JSON.stringify({ stage }),
      });
    } catch (err) {
      console.error('[SF] Stage update failed:', err);
    }
  },
};
